import { Mitarbeiter } from '../../entities';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMitarbeiter } from '../../hooks/useMitarbeiter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesUpDown,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import { MitarbeiterAvatar } from '../Avatar/MitarbeiterAvatar';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { cn } from '@/lib/utils';

// eslint-disable-next-line max-lines-per-function
export function MitarbeiterSelect({
  onChange,
  isUpdating,
  selectedMitarbeiter,
  datum,
}: {
  onChange?: (newMitarbeiter: Mitarbeiter) => void;
  isUpdating: boolean;
  selectedMitarbeiter?: Mitarbeiter;
  datum: string;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { mitarbeiter, error } = useMitarbeiter(standortId, datum);

  if (error) {
    return (
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-[275px] justify-between"
        disabled>
        {t('domain.arbeitszeiten.create.mitarbeiterError')}
        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-primary-red-500"
        />
      </Button>
    );
  }

  function handleSelect(newMitarbeiterId: string) {
    const selectedMitarbeiter = mitarbeiter?.find(
      (m) => m.id === newMitarbeiterId,
    );
    if (!selectedMitarbeiter) return;
    onChange?.(selectedMitarbeiter);
    setOpen(false);
  }

  function handleFilter(id: string, search: string) {
    const searchLower = search.toLowerCase();
    const filteredMitarbeiter = (mitarbeiter ?? []).filter((m) => {
      const name = m.vorname + ' ' + m.nachname;
      return name.toLowerCase().includes(searchLower);
    });
    return filteredMitarbeiter.some((m) => m.id === id) ? 1 : 0;
  }

  return (
    <Popover open={open} onOpenChange={(open) => setOpen(open)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline-secondary"
          role="combobox"
          className={cn(
            'w-[275px] justify-between font-normal  [&>svg]:text-neutrals-900',
            selectedMitarbeiter ? 'text-neutrals-900' : 'text-neutrals-700',
          )}>
          {selectedMitarbeiter
            ? `${selectedMitarbeiter?.vorname} ${selectedMitarbeiter?.nachname}`
            : t('domain.arbeitszeiten.create.mitarbeiterSelectPlaceholder')}
          <FontAwesomeIcon icon={faAnglesUpDown} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="bg-white p-0 max-w-[275px]">
        <Command filter={handleFilter}>
          <CommandInput disabled={isUpdating} placeholder="Suchen…" />
          <CommandList>
            <CommandEmpty className="text-center text-neutrals-700 pb-4 text-sm">
              {t('domain.arbeitszeiten.create.mitarbeiterSelectNotFound')}
            </CommandEmpty>
            <CommandGroup>
              {(mitarbeiter ?? []).map((m) => (
                <CommandItem
                  className="flex items-center gap-2 p-2"
                  value={m.id}
                  key={m.id}
                  onSelect={handleSelect}>
                  <MitarbeiterAvatar
                    vorname={m.vorname}
                    nachname={m.nachname}
                    extern={m.extern}
                    bildUrl={m.bildUrl}
                  />
                  <div className="truncate grow">{`${m.vorname} ${m.nachname}`}</div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
